import { useShow, useTranslate } from '@refinedev/core';
import { Show, DateField } from '@refinedev/antd';
import { Typography, Divider, Descriptions, Tag, Alert } from 'antd';
import type { WithdrawalContractDetailOut } from '@/api/generated/schemas';
import { ShowLink } from '@/components/common/ShowLink';
import { RevenueCatCustomerLink } from '@/components/monetization/RevenueCatLink';
import { WaiverStatusTag } from '@/components/monetization/withdrawal_contracts/WaiverStatusTag';

const { Title } = Typography;

const dash = (value: string | number | null | undefined) => value ?? '-';
const dateOrDash = (value: string | null | undefined) =>
  value ? <DateField value={value} format="LLL" /> : '-';
const tagOrDash = (value: string | null | undefined) =>
  value ? <Tag>{value}</Tag> : '-';

export const WithdrawalContractShow = () => {
  const t = useTranslate();
  const { query } = useShow<WithdrawalContractDetailOut>({
    resource: 'monetization.withdrawal_contracts',
  });
  const { data, isLoading } = query;
  const record = data?.data;
  const f = (key: string) =>
    t(`monetization.withdrawal_contracts.fields.${key}`);

  return (
    <Show isLoading={isLoading}>
      {record && record.waiver_id == null && (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
          message={t('monetization.withdrawal_contracts.alerts.no_waiver')}
        />
      )}
      {record &&
        record.waiver_id != null &&
        record.consent_fingerprint_valid === false && (
          <Alert
            type="error"
            showIcon
            style={{ marginBottom: 16 }}
            message={t(
              'monetization.withdrawal_contracts.alerts.invalid_fingerprint'
            )}
          />
        )}

      <Title level={4}>
        {t('monetization.withdrawal_contracts.sections.contract')}
      </Title>
      <Descriptions bordered column={2} size="small">
        <Descriptions.Item label={f('id')}>{record?.id}</Descriptions.Item>
        <Descriptions.Item label={f('event_id')}>
          {record ? (
            <ShowLink resource="monetization.revenuecat_events" id={record.id}>
              <span style={{ fontFamily: 'monospace' }}>{record.event_id}</span>
            </ShowLink>
          ) : (
            '-'
          )}
        </Descriptions.Item>
        <Descriptions.Item label={f('event_type')}>
          {tagOrDash(record?.event_type)}
        </Descriptions.Item>
        <Descriptions.Item label={f('event_timestamp')}>
          {dateOrDash(record?.event_timestamp)}
        </Descriptions.Item>
        <Descriptions.Item label={f('account_id')}>
          {dash(record?.account_id)}
        </Descriptions.Item>
        <Descriptions.Item label={f('app_user_id')}>
          {record?.app_user_id ? (
            <RevenueCatCustomerLink
              id={record.app_user_id}
              title={t('monetization.customers.actions.open_in_revenuecat')}
            />
          ) : (
            '-'
          )}
        </Descriptions.Item>
        <Descriptions.Item label={f('store')}>
          {tagOrDash(record?.store)}
        </Descriptions.Item>
        <Descriptions.Item label={f('environment')}>
          {record?.environment ? (
            <Tag
              color={
                record.environment === 'PRODUCTION' ? 'success' : 'warning'
              }
            >
              {record.environment}
            </Tag>
          ) : (
            '-'
          )}
        </Descriptions.Item>
        <Descriptions.Item label={f('product_id')}>
          {dash(record?.product_id)}
        </Descriptions.Item>
        <Descriptions.Item label={f('new_product_id')}>
          {dash(record?.new_product_id)}
        </Descriptions.Item>
        <Descriptions.Item label={f('period_type')}>
          {dash(record?.period_type)}
        </Descriptions.Item>
        <Descriptions.Item label={f('transaction_id')}>
          {record?.transaction_id ? (
            <span style={{ fontFamily: 'monospace' }}>
              {record.transaction_id}
            </span>
          ) : (
            '-'
          )}
        </Descriptions.Item>
      </Descriptions>

      <Divider />
      <Title level={4}>
        {t('monetization.withdrawal_contracts.sections.waiver')}
      </Title>
      <Descriptions bordered column={2} size="small">
        <Descriptions.Item label={f('waiver_status')}>
          {record ? (
            <WaiverStatusTag
              waiverId={record.waiver_id}
              fingerprintValid={record.consent_fingerprint_valid}
            />
          ) : (
            '-'
          )}
        </Descriptions.Item>
        <Descriptions.Item label={f('waiver_id')}>
          {dash(record?.waiver_id)}
        </Descriptions.Item>
        <Descriptions.Item label={f('consent_version')}>
          {tagOrDash(record?.consent_version)}
        </Descriptions.Item>
        <Descriptions.Item label={f('consent_fingerprint')}>
          {record?.consent_fingerprint ? (
            <span style={{ fontFamily: 'monospace', wordBreak: 'break-all' }}>
              {record.consent_fingerprint}
            </span>
          ) : (
            '-'
          )}
        </Descriptions.Item>
        <Descriptions.Item label={f('waived_at')}>
          {dateOrDash(record?.waived_at)}
        </Descriptions.Item>
        <Descriptions.Item label={f('matched_at')}>
          {dateOrDash(record?.matched_at)}
        </Descriptions.Item>
      </Descriptions>
    </Show>
  );
};
